import { ContractSpecificationModel } from '../../models/contracts/AgencyContract.model';
import { ContractStatus } from '../../models/businesses/BusinessContract.model';
import {
  ContractConfiguration, OpenProductContractMap,
  OpenProductName,
  ProductContractVariant,
  ValidContractSubscriptionKeys,
} from '../../models/products/Products.model';
import { manageError } from '../errors';

export const CONTRACT_SPECIFICATIONS: ContractConfiguration<ContractSpecificationModel> = {
  OPENEAT: {
    STANDARD: {
      openProductName: 'OPENEAT',
      productNature: 'FOOD_VOUCHER',
      creditRedemptionMethod: 'LINEAR',
      pricingMethod: 'PREPAID',
      transactionSources: ['SCAN_CB', 'OPEN_BANKING'],
      paymentTargets: ['BENEFICIARY'],
      useManagedPayment: true,
      useCustomMaskLists: false,
    },
    INTERIM: {
      openProductName: 'OPENEAT',
      productNature: 'FOOD_VOUCHER',
      creditRedemptionMethod: 'LINEAR',
      pricingMethod: 'PREPAID',
      transactionSources: ['SCAN_CB', 'OPEN_BANKING'],
      paymentTargets: ['BENEFICIARY'],
      useManagedPayment: true,
      useCustomMaskLists: false,
    },
    GEOGRAPHIC_RESTRICTION: {
      openProductName: 'OPENEAT',
      productNature: 'FOOD_VOUCHER',
      creditRedemptionMethod: 'LINEAR',
      pricingMethod: 'PREPAID',
      transactionSources: ['SCAN_CB', 'OPEN_BANKING'],
      paymentTargets: ['BENEFICIARY'],
      useManagedPayment: true,
      useCustomMaskLists: true,
    },
    ONE_SHOT: {
      openProductName: 'OPENEAT',
      productNature: 'FOOD_VOUCHER',
      creditRedemptionMethod: 'DISCRETE',
      pricingMethod: 'PREPAID',
      transactionSources: ['SCAN_CB'],
      paymentTargets: ['BENEFICIARY'],
      useManagedPayment: true,
      useCustomMaskLists: false,
    },
  },
  OPEN2PAY: {
    PUBLIC_MEAL_SUBSIDY: {
      openProductName: 'OPEN2PAY',
      productNature: 'FOOD_VOUCHER',
      creditRedemptionMethod: 'DISCRETE',
      pricingMethod: 'PAY_AS_YOU_GO',
      transactionSources: ['QR_CODE'],
      paymentTargets: ['BUSINESS_ENTITY'],
      useManagedPayment: false,
      useCustomMaskLists: false,
    },
  },
  OPENGIFT: {
    STANDARD: {
      openProductName: 'OPENGIFT',
      productNature: 'GIFT_VOUCHER',
      creditRedemptionMethod: 'LINEAR',
      pricingMethod: 'PREPAID',
      transactionSources: ['SCAN_CB'],
      paymentTargets: ['BENEFICIARY'],
      useManagedPayment: true,
      useCustomMaskLists: false,
    },
  },
  PRUNEO: {
    STANDARD: {
      openProductName: 'PRUNEO',
      productNature: 'GIFT_VOUCHER',
      creditRedemptionMethod: 'DISCRETE',
      pricingMethod: 'PREPAID',
      transactionSources: ['QR_CODE'],
      paymentTargets: ['BUSINESS_ENTITY'],
      // `true`: at contract creation, the financer wallet is onboarded and the ledger initialised.
      useManagedPayment: true,
      useCustomMaskLists: false,
    },
  },
};

export function listContractSpecificationsByProduct<OpenProduct extends OpenProductName>(
  openProductName: OpenProduct,
): ContractConfiguration<ContractSpecificationModel>[OpenProduct] {

  if (!(openProductName in CONTRACT_SPECIFICATIONS)) {
    throw manageError({
      code: 'invalid-argument',
      message: `Invalid Open product name: ${openProductName}`,
      statusCode: 400,
    });
  }

  return CONTRACT_SPECIFICATIONS[openProductName];
}

export function getContractSpecification<OpenProduct extends OpenProductName, ContractVariant extends ProductContractVariant<OpenProduct>>(
  openProductName: OpenProduct,
  contractVariant: ContractVariant,
  triggerError: true,
): ContractSpecificationModel;
export function getContractSpecification<OpenProduct extends OpenProductName, ContractVariant extends ProductContractVariant<OpenProduct>>(
  openProductName: OpenProduct,
  contractVariant: ContractVariant,
  triggerError?: false,
): ContractSpecificationModel | null;
export function getContractSpecification<OpenProduct extends OpenProductName, ContractVariant extends ProductContractVariant<OpenProduct>>(
  openProductName: OpenProduct,
  contractVariant: ContractVariant,
  triggerError = false,
): ContractSpecificationModel | null {
  const spec = CONTRACT_SPECIFICATIONS[openProductName]?.[contractVariant] ?? null;
  if (!spec && triggerError) {
    manageError({
      code: 'invalid-argument',
      message: `[getContractSpecification] Invalid contract type for product ${openProductName}: ${contractVariant}`,
      statusCode: 400,
    });
  }
  return spec;
}

export function mapContractSubscriptionKey<OpenProduct extends OpenProductName, ContractVariant extends ProductContractVariant<OpenProduct>>(
  openProductName: OpenProduct,
  contractVariant: ContractVariant,
): ValidContractSubscriptionKeys {

  assertIsProductContractTypeValid(openProductName, contractVariant);

  return `${openProductName}:${contractVariant}` as ValidContractSubscriptionKeys;
}

export function getAvailableProducts(): OpenProductName[] {
  return Object.keys(OpenProductContractMap) as OpenProductName[];
}

export function getAvailableContractVariants<OpenProduct extends OpenProductName>(
  openProductName: OpenProduct,
): ProductContractVariant<OpenProduct>[] {
  return [...OpenProductContractMap[openProductName]] as ProductContractVariant<OpenProduct>[];
}

/** Product eligibility depends only on the prefecture-group status — works on any structure that carries it (company, agency, sector). */
export function getEligibleOpenProducts(structure: { readonly isPrefectureGroup?: boolean }): OpenProductName[] {
  if (structure.isPrefectureGroup) {
    return ['OPEN2PAY'];
  }

  return ['OPENEAT', 'OPENGIFT', 'PRUNEO'];
}

export function assertIsProductContractTypeValid<OpenProduct extends OpenProductName, ContractVariant extends ProductContractVariant<OpenProduct>>(openProductName: OpenProduct, contractVariant: ContractVariant) {
  if (!isProductContractTypeValid(openProductName, contractVariant)) {
    throw manageError({
      code: 'invalid-argument',
      message: `Invalid contract type for product ${openProductName}: ${contractVariant}`,
      statusCode: 400,
    });
  }
}

export function isProductContractTypeValid<OpenProduct extends OpenProductName, ContractVariant extends ProductContractVariant<OpenProduct>>(openProductName: OpenProduct, contractVariant: ContractVariant): boolean {
  if (!(openProductName in OpenProductContractMap)) {
    return false;
  }

  // Cast: TS can't narrow `OpenProductContractMap[openProductName]` to a checkable array here.
  return (OpenProductContractMap[openProductName] as any).includes(contractVariant);
}

/** A contract in one of these statuses binds nobody any more: a new one may be opened beside it. */
export const NOT_IN_FORCE_CONTRACT_STATUSES: readonly ContractStatus[] = ['CANCELED', 'EXPIRED', 'ENDED', 'DELETED'];

export const PREPAID_PRODUCTS = ['OPENEAT', 'OPENGIFT', 'PRUNEO'] as const;
export type PrepaidProduct = typeof PREPAID_PRODUCTS[number];

/** Derived from {@link CONTRACT_SPECIFICATIONS} so it stays in sync — no second hardcoded list. */
export const QR_IDENTIFIER_PRODUCTS: OpenProductName[] = (Object.keys(CONTRACT_SPECIFICATIONS) as OpenProductName[])
  .filter((openProductName) =>
    Object.values(CONTRACT_SPECIFICATIONS[openProductName] as Record<string, ContractSpecificationModel>)
      .some((spec) => spec.transactionSources.includes('QR_CODE')),
  );
export const OPEN_PAID_MERCHANT_PRODUCTS: OpenProductName[] = (Object.keys(CONTRACT_SPECIFICATIONS) as OpenProductName[])
  .filter((openProductName) =>
    Object.values(CONTRACT_SPECIFICATIONS[openProductName] as Record<string, ContractSpecificationModel>)
      .some((spec) => spec.useManagedPayment && spec.paymentTargets.includes('BUSINESS_ENTITY')),
  );
